// scenarios/ScenarioContext.tsx — login-screen scenario selection state.
//
// Provides:
//   - selectedId: which scenario the user has clicked-to-pick. Persists.
//   - effective:  the presentation currently driving the page (== selectedId).
//   - ordered:    the pill-row iteration order.
//
// Click-to-select only. The previous hover-preview behaviour was removed
// 2026-05-11 — it was easy to trigger accidentally and obscured the actual
// click affordance. Pills now have a clear hover state but don't repaint
// the page until clicked.
//
// Scope: the unauthenticated/login surface only. App.tsx wraps the !authed
// branch in <ScenarioProvider>; the post-login game UI is intentionally NOT
// wrapped — the in-game look belongs to the user's actual saved story.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DEFAULT_SCENARIO_ID,
  SCENARIO_PRESENTATIONS,
  SCENARIO_PRESENTATION_ORDER,
  type ScenarioPresentation,
} from "./presentation";

interface ScenarioContextValue {
  /** The scenario the user has clicked. Persists until they click another. */
  selectedId: string;
  /** The presentation currently driving the page. Always SCENARIO_PRESENTATIONS[selectedId]. */
  effective: ScenarioPresentation;
  /** Iteration order for the pill row. */
  ordered: ScenarioPresentation[];
  setSelected: (id: string) => void;
}

const Ctx = createContext<ScenarioContextValue | null>(null);

interface ScenarioProviderProps {
  children: ReactNode;
  /** Override the initial selected id. Defaults to ember_hollow. */
  defaultId?: string;
}

export function ScenarioProvider({
  children,
  // Anonymous landing — no user known, so theme stays generic (ember_hollow)
  // until either a pill is clicked or signin identifies a user. Once a user
  // is identified, Login.tsx calls setSelected with their
  // foreverDefaultScenarioId so the surface snaps to their locked theme.
  defaultId = DEFAULT_SCENARIO_ID,
}: ScenarioProviderProps) {
  // Defensive: if a caller passes an unknown id, fall back to the default.
  const initialId = SCENARIO_PRESENTATIONS[defaultId]
    ? defaultId
    : DEFAULT_SCENARIO_ID;

  const [selectedId, setSelectedIdState] = useState<string>(initialId);

  // setSelected guards against unknown ids — the call would otherwise
  // produce an `effective` that's undefined and crash the render.
  const setSelected = useCallback((id: string) => {
    if (!SCENARIO_PRESENTATIONS[id]) return;
    setSelectedIdState(id);
  }, []);

  // Ordered list — memoised because it never changes shape after mount.
  const ordered = useMemo<ScenarioPresentation[]>(() => {
    return SCENARIO_PRESENTATION_ORDER.map(
      (id) => SCENARIO_PRESENTATIONS[id]
    ).filter((p): p is ScenarioPresentation => Boolean(p));
  }, []);

  // Effective presentation — the one the page actually paints with.
  const effective = useMemo<ScenarioPresentation>(() => {
    return (
      SCENARIO_PRESENTATIONS[selectedId] ??
      SCENARIO_PRESENTATIONS[DEFAULT_SCENARIO_ID]
    );
  }, [selectedId]);

  const value = useMemo<ScenarioContextValue>(
    () => ({
      selectedId,
      effective,
      ordered,
      setSelected,
    }),
    [selectedId, effective, ordered, setSelected]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Read the current scenario context. Throws if called outside a provider —
 * we want loud failures, not silent fallthroughs to default theming.
 */
export function useScenario(): ScenarioContextValue {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error(
      "useScenario must be used inside a <ScenarioProvider>. " +
        "App.tsx wraps the unauthenticated branch; if you're seeing this, " +
        "you imported a login-only component into the post-login UI."
    );
  }
  return v;
}
